import { hasRole, useUser } from "@procurement-agents/auth";
import { Badge, Button, Card } from "@procurement-agents/ui";
import type { QaOutcome, QaRetrospective as Report } from "@procurement-agents/scope-api";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useScopeApi } from "../api";
import { RISK_TONE, RISKS } from "../risk";

const OUTCOMES: QaOutcome[] = ["fixed", "dismissed", "carried to publish", "open"];
const day = (d: Date) => d.toISOString().slice(0, 10);

/** CHANGE_qa_risk: the post-approval review the 28 Sep meeting asked for — what QA found across requests, by risk,
 *  by outcome and by rule, with every dismiss reason; the rows download as CSV. Procurement only. */
export function QaRetrospective() {
  const { api, toast } = useScopeApi();
  const user = useUser();
  const [from, setFrom] = useState(() => day(new Date(Date.now() - 90 * 86_400_000)));
  const [to, setTo] = useState(() => day(new Date()));
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setReport(await api.qaRetrospective(from, to)); } catch (e) { toast(String((e as { detail?: string }).detail ?? e), "warn"); } finally { setLoading(false); }
  }, [api, from, to, toast]);
  useEffect(() => { void load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const download = async () => {
    const { blob, filename } = await api.qaRetrospectiveCsv(from, to);
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };
  if (!hasRole(user, "procurement_user")) return <p className="muted">The QA retrospective is for procurement.</p>;
  return (
    <div className="page wide qa-retro">
      <Link to=".." relative="path" className="back-link">← Scope requests</Link>
      <header className="row between wrap">
        <h2>QA retrospective</h2>
        <span className="row wrap">
          <label className="row small">From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" /></label>
          <label className="row small">To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" /></label>
          <Button variant="quiet" onClick={() => void load()} disabled={loading}>{loading ? "Loading…" : "Show"}</Button>
          <Button variant="quiet" onClick={() => void download()} disabled={!report?.findings}>Download CSV</Button>
        </span>
      </header>
      {!report ? <p className="muted">Loading…</p> : (
        <>
          <p className="muted small">{report.findings} finding{report.findings === 1 ? "" : "s"} across {report.runs} request{report.runs === 1 ? "" : "s"} updated in the period.</p>
          <Card title="By risk and outcome">
            <table className="table">
              <thead><tr><th scope="col">Risk</th>{OUTCOMES.map((o) => <th key={o} scope="col" className="num">{o}</th>)}</tr></thead>
              <tbody>{RISKS.map((r) => <tr key={r}><th scope="row"><Badge tone={RISK_TONE[r]}>{r}</Badge></th>{OUTCOMES.map((o) => <td key={o} className="num">{report.by_risk[r]?.[o] ?? 0}</td>)}</tr>)}</tbody>
            </table>
          </Card>
          <Card title="By rule">
            {report.by_rule.length === 0 ? <p className="muted">No findings in the period.</p> : (
              <table className="table">
                <thead><tr><th scope="col">Rule</th><th scope="col" className="num">Found</th>{OUTCOMES.map((o) => <th key={o} scope="col" className="num">{o}</th>)}</tr></thead>
                <tbody>{report.by_rule.map((r) => <tr key={r.rule}><td>{r.rule_text}</td><td className="num">{r.count}</td>{OUTCOMES.map((o) => <td key={o} className="num">{r[o] ?? 0}</td>)}</tr>)}</tbody>
              </table>
            )}
          </Card>
          <Card title="Why findings were dismissed">
            {report.dismissals.length === 0 ? <p className="muted">None dismissed in the period.</p> : (
              <ul className="dismissals">{report.dismissals.map((d, i) => <li key={i}><Badge tone={RISK_TONE[d.risk]}>{d.risk}</Badge> <strong>{d.rule_text}</strong> <span className="muted small">· {d.request} · {d.section}</span><br />{d.dismissed_reason}</li>)}</ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
